import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { UploadedFilesSearchForm } from "./uploaded-files-search-form";
import { UploadedFilesTable } from "./uploaded-files-table";
import { UploadedFileSummarySchema } from "@/domains/openapi/types";
import { Button } from "@/components/button";

export const UploadedFilesRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const initialOwner = searchParams.get("owner") ?? "";
  const [query, setQuery] = useState<string>(initialQuery);
  const [owner, setOwner] = useState<string>(initialOwner);

  useEffect(() => {
    setQuery(searchParams.get("query") ?? "");
    setOwner(searchParams.get("owner") ?? "");
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["uploaded-files-search", query, owner],
    queryFn: () =>
      api.post("/internal/v1/uploaded-files/search", {
        body: {
          query_string: query || undefined,
          filter_by_owner_space_account_id: owner || undefined,
          limit: pageSize,
        },
      }),
    enabled: query.length > 0 || owner.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleView = (id: string) => {
    navigate(atlasRoutes.uploadedFilesView({ params: { uploadedFileId: id } }).path);
  };

  const handleSearch = (q: string, o: string) => {
    if (q) searchParams.set("query", q);
    else searchParams.delete("query");
    if (o) searchParams.set("owner", o);
    else searchParams.delete("owner");
    setSearchParams(searchParams);
  };

  const tableData = useMemo<UploadedFileSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <FlexHeader>
        <div>
          <h1>Uploaded Files</h1>
          <DescriptionText>Search by filename or filter by owner space account.</DescriptionText>
        </div>
        <Button onClick={() => navigate(atlasRoutes.uploadedFilesCreate({}).path)}>
          Create Uploaded File
        </Button>
      </FlexHeader>
      <UploadedFilesSearchForm
        initialQuery={query}
        initialOwnerId={owner}
        onSearch={handleSearch}
      />
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching uploaded files: {error.message}</div>}
      {tableData.length > 0 ? (
        <UploadedFilesTable data={tableData} onViewUploadedFile={handleView} pageSize={pageSize} />
      ) : (
        !isLoading && (query || owner) && <div>No uploaded files found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const FlexHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;
